import React, { useState, useContext } from 'react';
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Grid,
    Tooltip
} from '@mui/material';
               // ⬅️  removed Tabs & Tab
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Header from '../components/Header';
import Footer from '../components/Footer';
import SnackbarAlert from '../components/SnackbarAlert';
import FileUpload from '../components/FileUpload';
import CommentContext from '../context/CommentContext';

import { useTheme } from '@mui/material/styles';

/* ---------- constants ---------- */

const sections = [
  {
    key: 'user_registration',
    label: 'Basic Information',
    fields: [
      { name: 'name', label: 'Organization name' },
      { name: 'size', label: 'Organization size' },
      { name: 'years', label: 'Years in business' },
      { name: 'origin', label: 'Country of origin' },
      { name: 'operation', label: 'Operation type' },
      { name: 'email', label: 'Organization email' }
    ],
  },
  /* ---------- SELF-ASSESSMENT ---------- */
  {
    key: 'self_assessment',
    label: 'Self Assessment',
    subTabs: [
      {
        key: 'gov_strat',
        label: 'Governance & Strategic Planning',
        /* NEW ⇣ questions with optional explanation */
        questions: [
          { name: 'bylaws',                 text: 'Does your organization have Bylaws?' },
          { name: 'articles',               text: 'Does your organization have Articles of Association?' },
          { name: 'org_structure',          text: 'Does your organization have a documented Organizational Structure?' },
          { name: 'elections',              text: 'Has there been elections for the governing board in the past 3 years?' },
          { name: 'mission',                text: 'Does your organization have a mission statement?',    needsExplanation: true },
          { name: 'vision',                 text: 'Does your organization have a vision statement?',     needsExplanation: true },
          { name: 'strategy',               text: 'Does your organization have a strategic plan / strategy?', needsExplanation: true },
          { name: 'strategy_success',       text: 'Does your organization measure its success in meeting its strategy?', needsExplanation: true },
        ],
      },
      /* keep the rest of your self-assessment tabs unchanged */
      { key: 'financial_management',
        label: 'Financial Management',
        questions: [
            { name: 'fin_external_oversight', text: 'Does your organization have external authorities providing oversight of the accounting operation?' },
            { name: 'fin_chart_accounts_system', text: 'Does your organization have both a chart of account and an electronic recording system?' },
            { name: 'fin_procurement_policies', text: 'Does your organization have financial policies & procurement procedures including segregation of duties?' },
            { name: 'fin_external_audit', text: 'Has your organization performed at least one official external audit in the last year?' },
            { name: 'fin_master_budget', text: 'Does your organization prepare a Master Budget annually?' },
            { name: 'fin_cost_allocation', text: 'Does your organization practice effort-based cost allocation?' },
            { name: 'fin_mof_number', text: 'Does your organization have a Financial Number from Ministry of Finance?', needsExplanation: true },
            { name: 'fin_timely_reporting', text: 'Does your organization submit timely reporting to authorities?' },
            { name: 'fin_bank_reconciliation', text: 'Do you regularly reconcile bank and cash balances?' }
          ]
          
          
     },
      { key: 'risk_management', 
        label: 'Risk Management and Compliance',
        questions: [
            { name: 'risk_policy', text: 'Does your organization have a risk management policy?' },
            { name: 'risk_policy_approval', text: 'Did governing bodies approve the risk management policy?' },
            { name: 'risk_periodic_review', text: 'Does your organization periodically review/update the risk management system?' },
            { name: 'risk_lessons_learned', text: 'Does your organization integrate lessons learned?' },
            { name: 'compliance_framework', text: 'Does your organization have a Compliance Framework?' },
            { name: 'compliance_moi', text: 'Does your organization comply with Ministry of Interior requirements?' },
            { name: 'compliance_roles_accountable', text: 'Are roles clearly compliant with accountability duties?' },
            { name: 'compliance_communication', text: 'Do you communicate compliance mechanisms effectively to staff?' }
          ]
     },
      { key: 'legal_management',
        label: 'Legal Management',
        questions: [
            { name: 'legal_registered', text: 'Is your organization officially registered?', needsExplanation: true },
            { name: 'legal_annual_docs', text: 'Has your organization submitted annual documentation to authorities?' },
            { name: 'legal_notify_amendments', text: 'Have you notified authorities about any amendments in your Articles of Association?' }
          ]
          
     },
      { key: 'community_centricity',
        label: 'Community Centricity',
        questions: [
            { name: 'comm_needs_mechanism', text: 'Does your organization have mechanisms to understand community needs?' },
            { name: 'comm_baseline_studies', text: 'Does your organization perform baseline studies?' },
            { name: 'comm_staff_training', text: 'Are staff trained on community engagement concepts?' },
            { name: 'comm_engagement_manual', text: 'Does your organization have a manual for community engagement?' },
            { name: 'comm_complaint_mechanism', text: 'Is there a complaint mechanism available to communities?' },
            { name: 'comm_vulnerability_analysis', text: 'Does your organization consider vulnerabilities in analyses?' },
            { name: 'comm_meal_system', text: 'Does your organization have a MEAL system?' },
            { name: 'comm_exit_strategy', text: 'Do you explicitly express an exit strategy in projects?' },
            { name: 'comm_sustainability_included', text: 'Is sustainability considered in program design?' },
            { name: 'comm_impact_reports', text: 'Do you regularly publish project impact reports?' }
          ]           
    },
      { key: 'staff_volunteers',
        label: 'Staff and Volunteers Management',
        questions: [
            { name: 'hr_plan_alignment', text: 'Does your HR plan align with your organizational strategy?' },
            { name: 'hr_kpis', text: 'Does your HR department have KPIs?' },
            { name: 'recruitment_public', text: 'Are positions announced publicly?' },
            { name: 'recruitment_structured_interview', text: 'Does your organization have structured interview questions?' },
            { name: 'recruitment_induction', text: 'Do new employees undergo induction?' },
            { name: 'volunteers_exist', text: 'Does your organization have volunteers?' },
            { name: 'volunteer_satisfaction', text: 'Does your organization evaluate volunteer satisfaction?' },
            { name: 'hr_nssf_registration', text: 'Are employees registered in NSSF?' },
            { name: 'hr_performance_reviews', text: 'Does your organization have clear performance appraisals?' },
            { name: 'volunteer_decision_participation', text: 'Are volunteers involved in organizational decision-making?' }
          ]          
    },
      { key: 'program_project',
        label: 'Program and Project Management',
        questions: [
            { name: 'pm_framework', text: 'Does your organization have documented PM framework?' },
            { name: 'pm_deliverables_defined', text: 'Do projects have clearly defined deliverables?' },
            { name: 'pm_detailed_schedules', text: 'Do you develop detailed schedules?' },
            { name: 'pm_execute_as_planned', text: 'Do you execute projects according to plans?' },
            { name: 'pm_resource_sufficiency', text: 'Does your organization have sufficient resources for projects?' },
            { name: 'pm_monitoring_eval', text: 'Do you evaluate actual vs planned results?' },
            { name: 'pm_lessons_learned', text: 'Are lessons learned documented and integrated?' },
            { name: 'pm_exit_strategies', text: 'Do you plan exit strategies?' },
            { name: 'pm_quality_management', text: 'Do you have quality management plans?' },
            { name: 'pm_systematic_lesson_docs', text: 'Do you systematically document lessons learned from projects?' }
          ]          
    },
      { key: 'resources_asset', 
        label: 'Resources and Asset Management',
        questions: [
            { name: 'asset_policy', text: 'Does your organization have an approved asset management policy?', needsExplanation: true },
            { name: 'asset_inventories', text: 'Does your organization maintain detailed resource inventories?' },
            { name: 'asset_investment_plans', text: 'Do you have asset investment plans?' },
            { name: 'asset_sharing_partnerships', text: 'Are asset-sharing partnerships established?' },
            { name: 'asset_inventory_checks', text: 'Is there a regular inventory check of organizational assets?' }
          ]          
     },
      { key: 'proposal_management',
        label: 'Proposal Management and Writing',
        questions: [
            { name: 'proposal_sops', text: 'Does your organization have SOPs for proposal management?' },
            { name: 'proposal_tracking_funding', text: 'Does your organization track funding opportunities?' },
            { name: 'proposal_donor_docs', text: 'Do you provide required documentation to donors?' },
            { name: 'proposal_technical_capacity', text: 'Does your organization have capacity for technical proposal writing?' },
            { name: 'proposal_budget_capacity', text: 'Do you have capacity for budget proposals?' },
            { name: 'proposal_design_objectives', text: 'Does project design link to strategic objectives?' },
            { name: 'proposal_strategic_alignment', text: 'Is your proposal writing aligned clearly to strategic objectives?' }
          ]
          
     },
      { key: 'communication_reporting',
        label: 'Communication and Reporting',
        questions: [
            { name: 'comm_strategy_exists', text: 'Does your organization have communication goals and strategy?' },
            { name: 'comm_strategy_aligned', text: 'Is communication strategy aligned with organization\'s strategy?' },
            { name: 'comm_online_presence', text: 'Do you have updated online presence?' },
            { name: 'reporting_accuracy', text: 'Does your organization report regularly and accurately?' },
            { name: 'transparency_funding_sources', text: 'Do you disclose funding sources publicly?' },
            { name: 'transparency_staff_names', text: 'Do you publish names of key staff publicly?' },
            { name: 'transparency_financials', text: 'Do you publicly disclose detailed financial information?' }
          ]
           },
    ],
  },

  { key: 'documents', label: 'Documents', isFileUpload: true },
];

const documentFields = [
  { key: 'foundation_structure_document', label: 'Foundation Structure Document' },
  { key: 'articles_of_association', label: 'Articles of Association' },
  { key: 'bylaws_document', label: 'Bylaws Document' },
  { key: 'accountability_to_affected_people', label: 'Accountability to Affected People' },
  { key: 'bylaws_policies_guidelines', label: 'Bylaws, Policies & Guidelines' },
  { key: 'child_protection_policy', label: 'Child Protection Policy' },
  { key: 'complaints_and_feedback', label: 'Complaints and Feedback' },
  { key: 'data_protection_and_sharing', label: 'Data Protection and Sharing' },
  { key: 'financial_management_manual', label: 'Financial Management Manual' },
  { key: 'organizational_structure_and_roles', label: 'Organizational Structure and Roles' },
  { key: 'procurement_manual_policy', label: 'Procurement Manual/Policy' },
  { key: 'volunteer_development_guide', label: 'Volunteer Development Guide' },
  { key: 'employee_manual', label: 'Employee Manual' },
];

/* ---------- component ---------- */

function FormPage() {
  const [sectionIndex, setSectionIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);

  const [formData, setFormData] = useState(() => {
    const initial = {};
    for (const section of sections) {
      if (section.fields) {
        initial[section.key] = {};
        section.fields.forEach((f) => (initial[section.key][f.name] = ''));
      }
      if (section.key === 'self_assessment') {
        initial.self_assessment = {};
        section.subTabs.forEach((sub) => {
          if (sub.questions) {
            sub.questions.forEach((q) => {
              initial.self_assessment[q.name] = '';
              if (q.needsExplanation) initial.self_assessment[`${q.name}_explanation`] = '';
            });
          } else {
            initial.self_assessment[sub.key] = '';
          }
        });
      }
    }
    return initial;
  });
  

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);

  const navigate = useNavigate();
  const { setComments } = useContext(CommentContext);

  const [documents, setDocuments] = useState({});

  

  /* ----- navigation ----- */

  const handleNext = () => {
    const current = sections[sectionIndex];
    if (current.key === 'self_assessment') {
      if (subIndex < current.subTabs.length - 1) {
        setSubIndex((i) => i + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubIndex(0);
        setSectionIndex((i) => i + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      setSectionIndex((i) => i + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  /* ----- field change ----- */

  const handleChange = (sectionKey, fieldName) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], [fieldName]: e.target.value },
    }));
  };

  /* ----- submit ----- */

  const handleSubmit = async () => {
    const { name } = formData.user_registration || {};
    if (!name) {
      setError('Please fill in required fields.');
      setShowError(true);
      return;
    }

    // Restructure formData for easier parsing on the backend
    const restructuredData = { ...formData };
    if (restructuredData.self_assessment) {
      const selfAssessmentSection = sections.find(s => s.key === 'self_assessment');
      if (selfAssessmentSection) {
        const newSelfAssessment = {};
        selfAssessmentSection.subTabs.forEach(sub => {
          if (sub.questions) {
            newSelfAssessment[sub.key] = {
              label: sub.label,
              questions: sub.questions.map(q => ({
                name: q.name,
                text: q.text,
                answer: restructuredData.self_assessment[q.name] || '',
                explanation: q.needsExplanation ? restructuredData.self_assessment[`${q.name}_explanation`] || '' : undefined
              }))
            };
          } else {
            newSelfAssessment[sub.key] = {
              label: sub.label,
              answer: restructuredData.self_assessment[sub.key] || ''
            };
          }
        });
        restructuredData.self_assessment = newSelfAssessment;
      }
    }

    const payload = new FormData();
    payload.append('json_data', JSON.stringify(restructuredData));
    // Attach each document as its own field
    Object.entries(documents).forEach(([key, file]) => {
      if (file) payload.append(key, file);
    });

    navigate('/evaluation');

    try {
      const res = await axios.post('/evaluate_form', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data) {
        setComments({
          ...res.data,
          user_registration: restructuredData.user_registration, // <-- carries the email forward
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleCloseError = () => setShowError(false);

  // Stepper labels
  const stepLabels = sections.map((s) => s.label);

  const areAllFieldsFilled = () => {
    const currentSection = sections[sectionIndex];

    // For simple fields
    if (currentSection.fields) {
      return currentSection.fields.every((field) => {
        const value = formData[currentSection.key][field.name];
        return value !== undefined && value !== null && value.toString().trim() !== '';
      });
    }
    return true;
  }

  const theme = useTheme();

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Header title="Application Form" sx={{ color: theme.palette.text.white, fontFamily: theme.typography.fontFamily, mb: 1 }}/>
      <SnackbarAlert error={error} showError={showError} handleCloseError={handleCloseError} />

      <Paper elevation={4} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, backgroundColor: theme.palette.background.paper, maxWidth: 700, mx: 'auto' }}>
        <Stepper activeStep={sectionIndex} alternativeLabel sx={{ mb: 4 }}>
          {stepLabels.map((label, idx) => (
            <Step key={label} completed={sectionIndex > idx}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    color: theme.palette.text.primary,
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 700,
                  },
                  '& .MuiStepIcon-root': {
                    color: `${theme.palette.primary.main} !important`,
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box display="flex" flexDirection="column" gap={3}>
          <Typography variant="h5" sx={{ fontFamily: theme.typography.fontFamily, color: theme.palette.text.red, fontWeight: 700, mb: 1 }}>
            {sections[sectionIndex].label}
          </Typography>

          {/* SECTION BODY */}
          {sections[sectionIndex].fields ? (
            <Grid container spacing={2}>
              {sections[sectionIndex].fields.map((f) => (
                <Grid item xs={12} sm={6} key={f.name}>
                  <TextField
                    label={f.label}
                    placeholder={`Enter ${f.label.toLowerCase()}`}
                    fullWidth
                    variant="outlined"
                    value={formData[sections[sectionIndex].key][f.name]}
                    onChange={handleChange(sections[sectionIndex].key, f.name)}
                    InputLabelProps={{
                      sx: {
                        fontFamily: theme.typography.fontFamily,
                        color: theme.palette.text.primary,
                        '&.Mui-focused': { color: theme.palette.text.primary },
                      },
                    }}
                    InputProps={{
                      sx: {
                        fontFamily: theme.typography.fontFamily,
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 1,
                        '& input::placeholder': {
                          color: theme.palette.text.secondary,
                          opacity: 1,
                        },
                        '& input:-webkit-autofill': {
                          WebkitTextFillColor: theme.palette.text.primary,
                          transition: 'background-color 5000s ease-in-out 0s',
                          boxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.paper },
                      '& .MuiInputBase-input': { color: theme.palette.text.primary },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : sections[sectionIndex].isFileUpload ? (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily, mb: 1 }}>
                Please upload the required documents:
              </Typography>
                {documentFields.map((doc) => (
                  <Box key={doc.key} mb={2}>
                    <Typography sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily, mb: 1 }}>
                      {doc.label}
                    </Typography>
                    <FileUpload
                      uploadedFiles={documents[doc.key] ? [documents[doc.key]] : []}
                      onDrop={(files) => {
                        if (files && files[0]) {
                          setDocuments((prev) => ({ ...prev, [doc.key]: files[0] }));
                        }
                      }}
                    />
                  </Box>
                ))}
            </Box>
          ) : sections[sectionIndex].key === 'self_assessment' ? (
            (() => {
              const currentSub = sections[sectionIndex].subTabs[subIndex];
              return (
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="subtitle1" sx={{ fontFamily: theme.typography.fontFamily, color: theme.palette.text.primary, fontWeight: 600 }}>
                      {currentSub.label}
                    </Typography>
                    <Typography sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily, fontWeight: 500 }}>
                      {subIndex + 1} / {sections[sectionIndex].subTabs.length}
                    </Typography>
                  </Box>
                  {currentSub.questions ? (
                    <Box display="flex" flexDirection="column" gap={3}>
                      {currentSub.questions.map((q) => (
                        <Paper key={q.name} elevation={1} sx={{ p: 2, backgroundColor: theme.palette.background.paper, borderRadius: 2, mb: 2 }}>
                          <Typography sx={{ fontFamily: theme.typography.fontFamily, color: theme.palette.text.primary, mb: 1, fontWeight: 500 }}>
                            {q.text}
                          </Typography>
                          <Select
                            fullWidth
                            value={formData.self_assessment[q.name]}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                self_assessment: { ...prev.self_assessment, [q.name]: e.target.value },
                              }))
                            }
                            sx={{
                              backgroundColor: theme.palette.background.paper,
                              fontFamily: theme.typography.fontFamily,
                              color: theme.palette.text.primary,
                              borderRadius: 1,
                            }}
                          >
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                            <MenuItem value="Partially">Partially</MenuItem>
                          </Select>
                          {q.needsExplanation && (
                            <TextField
                              label="Explanation"
                              placeholder="Please explain..."
                              multiline
                              minRows={3}
                              fullWidth
                              variant="outlined"
                              value={formData.self_assessment[`${q.name}_explanation`]}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  self_assessment: {
                                    ...prev.self_assessment,
                                    [`${q.name}_explanation`]: e.target.value,
                                  },
                                }))
                              }
                              InputLabelProps={{
                                sx: {
                                  fontFamily: theme.typography.fontFamily,
                                  color: theme.palette.text.primary,
                                  '&.Mui-focused': { color: theme.palette.text.primary },
                                },
                              }}
                              InputProps={{
                                sx: {
                                  fontFamily: theme.typography.fontFamily,
                                  color: theme.palette.text.primary,
                                  backgroundColor: theme.palette.background.paper,
                                  borderRadius: 1,
                                  '& textarea::placeholder': {
                                    color: theme.palette.text.secondary,
                                    opacity: 1,
                                  },
                                },
                              }}
                              sx={{
                                mt: 2,
                                '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.paper },
                                '& .MuiInputBase-input': { color: theme.palette.text.primary },
                              }}
                            />
                          )}
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    <TextField
                      label="Self Assessment"
                      multiline
                      minRows={4}
                      fullWidth
                      variant="outlined"
                      value={formData.self_assessment[currentSub.key]}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          self_assessment: { ...prev.self_assessment, [currentSub.key]: e.target.value },
                        }))
                      }
                      InputLabelProps={{ sx: { fontFamily: theme.typography.fontFamily, color: theme.palette.text.primary } }}
                      InputProps={{
                        sx: {
                          fontFamily: theme.typography.fontFamily,
                          color: theme.palette.text.primary,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: 1,
                        },
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.paper } }}
                    />
                  )}
                </Box>
              );
            })()
          ) : null}

          {/* Navigation Buttons */}
          <Box display="flex" justifyContent="space-between" mt={4}>
            <Tooltip title={sectionIndex === 0 ? '' : 'Go to previous section'} arrow>
              <span>
                <Button
                  variant="outlined"
                  disabled={sectionIndex === 0 || (sections[sectionIndex].key === 'self_assessment' && subIndex === 0 && sectionIndex === 0)}
                  onClick={() => {
                    const current = sections[sectionIndex];
                    if (current.key === 'self_assessment') {
                      if (subIndex > 0) {
                        setSubIndex((i) => i - 1);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        return;
                      } else {
                        setSubIndex(0);
                        setSectionIndex((i) => i - 1);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        return;
                      }
                    }
                    setSectionIndex((i) => i - 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.text.primary,
                    minWidth: 120,
                  }}
                >
                  Back
                </Button>
              </span>
            </Tooltip>
            {sectionIndex < sections.length - 1 ? (
              <Tooltip title="Go to next section" arrow>
                <span>
                  <Button
                    variant="contained"
                    disabled={!areAllFieldsFilled()}
                    onClick={() => {
                      handleNext();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    sx={{
                      fontFamily: theme.typography.fontFamily,
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.text.opposite,
                      minWidth: 120,
                    }}
                  >
                    Next
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Submit your application for evaluation" arrow>
                <span>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleSubmit();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    sx={{
                      fontFamily: theme.typography.fontFamily,
                      backgroundColor: theme.palette.success.main,
                      color: theme.palette.text.opposite,
                      minWidth: 180,
                      fontWeight: 700,
                      '&:hover': {
                        backgroundColor: theme.palette.success.hover,
                      },
                    }}
                  >
                    Submit for Evaluation
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>
      <Footer />
    </Container>
  );
};

export default FormPage;
